import { ReactNode } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Alert } from "@mui/material";
import { ZodError } from "zod";

import i18n from "../i18n";

export enum ERROR_TYPE {
	INVALID_EMAIL = "INVALID_EMAIL",
	INVALID_PASSWORD = "INVALID_PASSWORD",
	PASSWORD_GUIDELINES = "PASSWORD_GUIDELINES",
	ACCEPT_TERMS = "ACCEPT_TERMS",
	INVALID_VALUE = "INVALID_VALUE",
	INVALID_CREDENTIALS = "INVALID_CREDENTIALS",
	PASSWORD_NOT_SET = "PASSWORD_NOT_SET",
	NOT_CONFIRMED = "NOT_CONFIRMED.",
	USER_NOT_FOUND = "USER_NOT_FOUND",
	EMAIL_ALREADY_TAKEN = "EMAIL_ALREADY_TAKEN",
	REQUIRED = "REQUIRED",
	PASSWORD_MISMATCH = "PASSWORD_MISMATCH",
	SOMETHING_WRONG = "SOMETHING_WRONG",
	INVALID_TOKEN = "INVALID_TOKEN",
	UNAUTHORIZED = "UNAUTHORIZED",
	INVALID_OTP_AUTH_CODE = "INVALID_OTP_AUTH_CODE",
	NAME_MISSING = "NAME_MISSING",
	EXPECTED_NUMBER = "Expected number, received nan",
	CUSTOM_COLUMN_ALREADY_EXISTS = "CUSTOM_COLUMN_ALREADY_EXISTS",
	WRONG_URL_SHOULD_BE_LINKEDIN_PROFILE = "WRONG_URL_SHOULD_BE_LINKEDIN_PROFILE",
	NOT_ENOUGH_CREDITS = "NOT_ENOUGH_CREDITS",
	LEAD_DATABASE_UNAVAILABLE = "LEAD_DATABASE_UNAVAILABLE",
	LINKEDIN_SUGGESTIONS_UNAVAILABLE = "LINKEDIN_SUGGESTIONS_UNAVAILABLE",
	MAX_AMOUNT_EXCEEDED = "MAX_AMOUNT_EXCEEDED",
	INVALID_LINKEDIN_URL = "INVALID_LINKEDIN_URL",
	ACCOUNT_IS_BLOCKED = "ACCOUNT_IS_BLOCKED",
	INVALID_URL = "INVALID_URL",
	INVALID_LINKEDIN_EVENT_URL = "INVALID_LINKEDIN_EVENT_URL",
	COLUMN_NAME_NO_UNDERSCORE = "COLUMN_NAME_NO_UNDERSCORE",
	SET_PASSWORD_LINK_INVALID = "SET_PASSWORD_LINK_INVALID",
	SET_PASSWORD_LINK_EXPIRED = "SET_PASSWORD_LINK_EXPIRED",
	SET_PASSWORD_LINK_USED = "SET_PASSWORD_LINK_USED",
	RESET_PASSWORD_LINK_INVALID = "RESET_PASSWORD_LINK_INVALID",
	RESET_PASSWORD_LINK_EXPIRED = "RESET_PASSWORD_LINK_EXPIRED",
	RESET_PASSWORD_LINK_USED = "RESET_PASSWORD_LINK_USED",
	SET_PASSWORD_LINK_MISSING = "SET_PASSWORD_LINK_MISSING",
	RESET_PASSWORD_LINK_MISSING = "RESET_PASSWORD_LINK_MISSING",
	LINKEDIN_TEST_REQUIRES_CONNECTION = "LINKEDIN_TEST_REQUIRES_CONNECTION",
	LINKEDIN_PROFILE_NOT_FOUND = "LINKEDIN_PROFILE_NOT_FOUND",
	LINKEDIN_SUBSCRIPTION_REQUIRED = "LINKEDIN_SUBSCRIPTION_REQUIRED",
	LINKEDIN_RECIPIENT_UNREACHABLE = "LINKEDIN_RECIPIENT_UNREACHABLE",
	LINKEDIN_NOT_CONNECTED = "LINKEDIN_NOT_CONNECTED",
	LINKEDIN_ACCOUNT_DISCONNECTED = "LINKEDIN_ACCOUNT_DISCONNECTED",
	LINKEDIN_RATE_LIMITED = "LINKEDIN_RATE_LIMITED",
	UNIPILE_REQUEST_FAILED = "UNIPILE_REQUEST_FAILED",
	AI_PROVIDER_FAILED = "AI_PROVIDER_FAILED",
	KNOWLEDGE_DB_BUILD_NOT_RETRYABLE = "KNOWLEDGE_DB_BUILD_NOT_RETRYABLE",
	CAMPAIGN_END_DATE_PASSED = "CAMPAIGN_END_DATE_PASSED",
	// Run now / list lifecycle refusals (PREC-4537), the same codes the API answers with.
	LIST_NOT_ACTIVE = "LIST_NOT_ACTIVE",
	LIST_IS_STOPPING = "LIST_IS_STOPPING",
	LIST_ARCHIVED = "LIST_ARCHIVED",
	LIST_NOT_IDLE = "LIST_NOT_IDLE",
	LIST_IS_PROCESSING = "LIST_IS_PROCESSING",
	// Exclude from list for a lead the app never offers it for (400, PREC-4537).
	LEAD_NOT_EXCLUDABLE = "LEAD_NOT_EXCLUDABLE",
	// Activating a list whose setup the app still calls incomplete (400, PREC-4537).
	LIST_SETUP_SCRAPE_NOT_REQUESTED = "LIST_SETUP_SCRAPE_NOT_REQUESTED",
	LIST_SETUP_SCRAPE_ONGOING = "LIST_SETUP_SCRAPE_ONGOING",
	LIST_SETUP_SCRAPE_UNCONFIRMED = "LIST_SETUP_SCRAPE_UNCONFIRMED",
	LIST_SETUP_SOURCE_MISSING = "LIST_SETUP_SOURCE_MISSING",
	LIST_SETUP_SOURCE_MANUAL_ONLY = "LIST_SETUP_SOURCE_MANUAL_ONLY",
	LIST_SETUP_SIGNALS_MISSING = "LIST_SETUP_SIGNALS_MISSING",
	LIST_SETUP_FIND_PERSON_MISSING = "LIST_SETUP_FIND_PERSON_MISSING",
	// A Select or Replace lost to someone who filled the row first (409, PREC-4570).
	ROW_NO_LONGER_PLACEHOLDER = "ROW_NO_LONGER_PLACEHOLDER",
	// A webhook URL pasted with the table's mask still in it (400, PREC-4253).
	WEBHOOK_URL_MASKED = "WEBHOOK_URL_MASKED",
	// A smart-list filter names a list that is not PUBLIC; its daily run cannot see it (400, PREC-4723).
	SMART_LIST_PRIVATE_LIST = "SMART_LIST_PRIVATE_LIST",
	// The one-time reveal of a signing secret was used already (409, PREC-4249).
	WEBHOOK_SECRET_ALREADY_REVEALED = "WEBHOOK_SECRET_ALREADY_REVEALED",
	// "Send test event" again within 15 seconds (429, PREC-4249).
	WEBHOOK_TEST_EVENT_TOO_SOON = "WEBHOOK_TEST_EVENT_TOO_SOON",
	// "Send test event" to a URL on a private network address (422, PREC-4249).
	WEBHOOK_TEST_TARGET_PRIVATE = "WEBHOOK_TEST_TARGET_PRIVATE",
	// A webhook saved with a URL on a private network address (400, PREC-4755).
	WEBHOOK_URL_PRIVATE = "WEBHOOK_URL_PRIVATE",
	// A webhook saved with a URL that is not http(s) (400, PREC-4755).
	WEBHOOK_URL_INVALID = "WEBHOOK_URL_INVALID",
	// An ordinary LinkedIn message to a lead whose invitation from this sender is pending (400,
	// PREC-4216); the shared-types `LINKEDIN_INVITATION_PENDING_ERROR`.
	LINKEDIN_INVITATION_PENDING = "LINKEDIN_INVITATION_PENDING",
	// The same message is already parked behind the same invitation (409, PREC-4216); the
	// shared-types `PARKED_MESSAGE_DUPLICATE_ERROR`.
	PARKED_MESSAGE_DUPLICATE = "PARKED_MESSAGE_DUPLICATE",
	// A batch whose messages cannot all wait behind one pending invitation: a later message is
	// another invitation, or comes from a second sender with a pending invitation (400, PREC-4216).
	INVITATION_BATCH_UNSUPPORTED = "INVITATION_BATCH_UNSUPPORTED",
}

export const translateError = {
	[ERROR_TYPE.INVALID_EMAIL]: i18n.t("errors.invalidEmail"),
	[ERROR_TYPE.INVALID_PASSWORD]: i18n.t("errors.invalidPassword"),
	[ERROR_TYPE.PASSWORD_GUIDELINES]: i18n.t("errors.passwordGuidelinesNotMet"),
	[ERROR_TYPE.ACCEPT_TERMS]: i18n.t("errors.termsAcceptanceMissing"),
	[ERROR_TYPE.INVALID_VALUE]: i18n.t("errors.invalidValue"),
	[ERROR_TYPE.INVALID_CREDENTIALS]: i18n.t("login.invalidData"),
	[ERROR_TYPE.PASSWORD_NOT_SET]: i18n.t("login.passwordNotSet"),
	[ERROR_TYPE.NOT_CONFIRMED]: i18n.t("login.userNotConfirmed"),
	[ERROR_TYPE.PASSWORD_MISMATCH]: i18n.t("errors.passwordMismatch"),
	[ERROR_TYPE.USER_NOT_FOUND]: i18n.t("login.invalidData"),
	[ERROR_TYPE.INVALID_TOKEN]: i18n.t("errors.invalidToken"),
	[ERROR_TYPE.EMAIL_ALREADY_TAKEN]: i18n.t("errors.emailAlreadyTaken"),
	[ERROR_TYPE.REQUIRED]: i18n.t("errors.requiredFields"),
	[ERROR_TYPE.SOMETHING_WRONG]: i18n.t("errors.wentWrong"),
	[ERROR_TYPE.UNAUTHORIZED]: i18n.t("errors.unauthorized"),
	[ERROR_TYPE.INVALID_OTP_AUTH_CODE]: i18n.t("errors.invalidAuthCode"),
	[ERROR_TYPE.NAME_MISSING]: i18n.t("errors.nameMissing"),
	[ERROR_TYPE.EXPECTED_NUMBER]: i18n.t("errors.expectedNumber"),
	[ERROR_TYPE.CUSTOM_COLUMN_ALREADY_EXISTS]: i18n.t("errors.customColumnAlreadyExists"),
	[ERROR_TYPE.WRONG_URL_SHOULD_BE_LINKEDIN_PROFILE]: i18n.t(
		"errors.wrongUrlShouldBeLinkedinProfile"
	),
	[ERROR_TYPE.NOT_ENOUGH_CREDITS]: i18n.t("errors.notEnoughCredits"),
	[ERROR_TYPE.LEAD_DATABASE_UNAVAILABLE]: i18n.t("errors.leadDatabaseUnavailable"),
	[ERROR_TYPE.LINKEDIN_SUGGESTIONS_UNAVAILABLE]: i18n.t("errors.linkedinSuggestionsUnavailable"),
	[ERROR_TYPE.MAX_AMOUNT_EXCEEDED]: i18n.t("errors.maxAmountExceeded"),
	[ERROR_TYPE.INVALID_LINKEDIN_URL]: i18n.t("errors.invalidLinkedinUrl"),
	[ERROR_TYPE.ACCOUNT_IS_BLOCKED]: i18n.t("errors.accountIsBlocked"),
	[ERROR_TYPE.INVALID_URL]: i18n.t("errors.invalidUrl"),
	[ERROR_TYPE.INVALID_LINKEDIN_EVENT_URL]: i18n.t("errors.invalidLinkedinEventUrl"),
	[ERROR_TYPE.COLUMN_NAME_NO_UNDERSCORE]: i18n.t("errors.columnNameNoUnderscore"),
	[ERROR_TYPE.SET_PASSWORD_LINK_INVALID]: i18n.t("errors.setPasswordLinkInvalid"),
	[ERROR_TYPE.SET_PASSWORD_LINK_EXPIRED]: i18n.t("errors.setPasswordLinkExpired"),
	[ERROR_TYPE.SET_PASSWORD_LINK_USED]: i18n.t("errors.setPasswordLinkUsed"),
	[ERROR_TYPE.RESET_PASSWORD_LINK_INVALID]: i18n.t("errors.resetPasswordLinkInvalid"),
	[ERROR_TYPE.RESET_PASSWORD_LINK_EXPIRED]: i18n.t("errors.resetPasswordLinkExpired"),
	[ERROR_TYPE.RESET_PASSWORD_LINK_USED]: i18n.t("errors.resetPasswordLinkUsed"),
	[ERROR_TYPE.SET_PASSWORD_LINK_MISSING]: i18n.t("errors.setPasswordLinkMissing"),
	[ERROR_TYPE.RESET_PASSWORD_LINK_MISSING]: i18n.t("errors.resetPasswordLinkMissing"),
	[ERROR_TYPE.LINKEDIN_TEST_REQUIRES_CONNECTION]: i18n.t("errors.linkedinTestRequiresConnection"),
	[ERROR_TYPE.LINKEDIN_PROFILE_NOT_FOUND]: i18n.t("errors.linkedinProfileNotFound"),
	[ERROR_TYPE.LINKEDIN_SUBSCRIPTION_REQUIRED]: i18n.t("errors.linkedinSubscriptionRequired"),
	[ERROR_TYPE.LINKEDIN_RECIPIENT_UNREACHABLE]: i18n.t("errors.linkedinRecipientUnreachable"),
	[ERROR_TYPE.LINKEDIN_NOT_CONNECTED]: i18n.t("errors.linkedinNotConnected"),
	[ERROR_TYPE.LINKEDIN_ACCOUNT_DISCONNECTED]: i18n.t("errors.linkedinAccountDisconnected"),
	[ERROR_TYPE.LINKEDIN_RATE_LIMITED]: i18n.t("errors.linkedinRateLimited"),
	[ERROR_TYPE.LINKEDIN_INVITATION_PENDING]: i18n.t("inbox.pendingInvitation.refused"),
	[ERROR_TYPE.PARKED_MESSAGE_DUPLICATE]: i18n.t("inbox.pendingInvitation.duplicate"),
	[ERROR_TYPE.INVITATION_BATCH_UNSUPPORTED]: i18n.t("inbox.pendingInvitation.batchUnsupported"),
	[ERROR_TYPE.UNIPILE_REQUEST_FAILED]: i18n.t("errors.unipileRequestFailed"),
	[ERROR_TYPE.AI_PROVIDER_FAILED]: i18n.t("errors.aiProviderFailed"),
	[ERROR_TYPE.KNOWLEDGE_DB_BUILD_NOT_RETRYABLE]: i18n.t("errors.knowledgeDbBuildNotRetryable"),
	[ERROR_TYPE.CAMPAIGN_END_DATE_PASSED]: i18n.t("errors.campaignEndDatePassed"),
	[ERROR_TYPE.LIST_NOT_ACTIVE]: i18n.t("errors.listNotActive"),
	[ERROR_TYPE.LIST_IS_STOPPING]: i18n.t("errors.listIsStopping"),
	[ERROR_TYPE.LIST_ARCHIVED]: i18n.t("errors.listArchived"),
	[ERROR_TYPE.LIST_NOT_IDLE]: i18n.t("errors.listNotIdle"),
	[ERROR_TYPE.LIST_IS_PROCESSING]: i18n.t("errors.listIsProcessing"),
	[ERROR_TYPE.LEAD_NOT_EXCLUDABLE]: i18n.t("errors.leadNotExcludable"),
	[ERROR_TYPE.LIST_SETUP_SCRAPE_NOT_REQUESTED]: i18n.t("errors.listSetupScrapeNotRequested"),
	[ERROR_TYPE.LIST_SETUP_SCRAPE_ONGOING]: i18n.t("errors.listSetupScrapeOngoing"),
	[ERROR_TYPE.LIST_SETUP_SCRAPE_UNCONFIRMED]: i18n.t("errors.listSetupScrapeUnconfirmed"),
	[ERROR_TYPE.LIST_SETUP_SOURCE_MISSING]: i18n.t("errors.listSetupSourceMissing"),
	[ERROR_TYPE.LIST_SETUP_SOURCE_MANUAL_ONLY]: i18n.t("errors.listSetupSourceManualOnly"),
	[ERROR_TYPE.LIST_SETUP_SIGNALS_MISSING]: i18n.t("errors.listSetupSignalsMissing"),
	[ERROR_TYPE.LIST_SETUP_FIND_PERSON_MISSING]: i18n.t("errors.listSetupFindPersonMissing"),
	[ERROR_TYPE.ROW_NO_LONGER_PLACEHOLDER]: i18n.t("errors.rowNoLongerPlaceholder"),
	[ERROR_TYPE.WEBHOOK_URL_MASKED]: i18n.t("errors.webhookUrlMasked"),
	[ERROR_TYPE.SMART_LIST_PRIVATE_LIST]: i18n.t("errors.smartListPrivateList"),
	[ERROR_TYPE.WEBHOOK_SECRET_ALREADY_REVEALED]: i18n.t("errors.webhookSecretAlreadyRevealed"),
	[ERROR_TYPE.WEBHOOK_TEST_EVENT_TOO_SOON]: i18n.t("errors.webhookTestEventTooSoon"),
	[ERROR_TYPE.WEBHOOK_TEST_TARGET_PRIVATE]: i18n.t("errors.webhookTestTargetPrivate"),
	[ERROR_TYPE.WEBHOOK_URL_PRIVATE]: i18n.t("errors.webhookUrlPrivate"),
	[ERROR_TYPE.WEBHOOK_URL_INVALID]: i18n.t("errors.webhookUrlInvalid"),
};

export const normalizeErrorMessage = (message: unknown): ERROR_TYPE => {
	if (Array.isArray(message)) {
		return normalizeErrorMessage(message[0]);
	}

	if (typeof message !== "string") {
		return ERROR_TYPE.SOMETHING_WRONG;
	}

	const upperCaseMessage = message.toUpperCase();
	const matchingError = Object.values(ERROR_TYPE).find(
		(errorType) => errorType === message || errorType === upperCaseMessage
	);

	return matchingError ?? ERROR_TYPE.SOMETHING_WRONG;
};

export const renderErrorMessages = (errors: ERROR_TYPE[]): ReactNode => {
	const uniqueErrors: ERROR_TYPE[] = errors.filter((err, index, arr) => arr.indexOf(err) === index);

	return (
		<>
			{uniqueErrors.length === 1 ? (
				<Alert severity="error" sx={{ alignItems: "center", bgcolor: "error.light", fontSize: 14 }}>
					{translateError[uniqueErrors[0]] ?? translateError[ERROR_TYPE.SOMETHING_WRONG]}
				</Alert>
			) : (
				uniqueErrors.map((error, index) => (
					<Alert
						key={index}
						severity="error"
						sx={{ mb: 1, alignItems: "center", bgcolor: "error.light", fontSize: 14 }}
					>
						{translateError[error] ?? translateError[ERROR_TYPE.SOMETHING_WRONG]}
					</Alert>
				))
			)}
		</>
	);
};

export const getErrorMessage = (error: FetchBaseQueryError | SerializedError): ERROR_TYPE => {
	if ("data" in error) {
		const { data } = error;
		const message =
			data && typeof data === "object" && "message" in data
				? (data as { message?: unknown }).message
				: null;

		return normalizeErrorMessage(message);
	}

	try {
		const parsedErrors = JSON.parse((error as ZodError).message || "[]");
		const message = Array.isArray(parsedErrors) ? parsedErrors[0]?.message : parsedErrors;

		return normalizeErrorMessage(message);
	} catch {
		return normalizeErrorMessage((error as SerializedError).message);
	}
};

/**
 * The sentence for a rejected request, from the code the API answered with: "Not enough credits"
 * for a 402 NOT_ENOUGH_CREDITS (PREC-4437), "Something went wrong" for anything it does not know.
 */
export const translateRequestError = (error: unknown): string =>
	translateError[getErrorMessage((error ?? {}) as FetchBaseQueryError | SerializedError)] ??
	translateError[ERROR_TYPE.SOMETHING_WRONG];

export const getAllErrors = (
	serverError: FetchBaseQueryError | SerializedError | undefined,
	formErrors: ERROR_TYPE[]
): ERROR_TYPE[] => {
	return [
		serverError ? getErrorMessage(serverError) : null,
		...formErrors.filter((e) => !!e).map((e) => normalizeErrorMessage(e)),
	].filter(Boolean) as ERROR_TYPE[];
};
